import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { ROLE_OPTIONS, roleLabel } from "@/lib/roles";
import { Plus, Save, ClipboardList, Users, Settings as SettingsIcon, ScrollText, Building2, MapPin, Sparkles, Trash2, Monitor, Eye, KeyRound, Scale, Truck, LayoutGrid } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const TABS = [
  { key: "users", label: "Users", icon: Users },
  { key: "stores", label: "Stores", icon: Building2 },
  { key: "registers", label: "Registers", icon: Monitor },
  { key: "suppliers", label: "Suppliers", icon: Truck },
  { key: "display_areas", label: "Display Areas", icon: LayoutGrid },
  { key: "zones", label: "Zones", icon: MapPin },
  { key: "fandoms", label: "Fandoms", icon: Sparkles },
  { key: "standards", label: "Display Standards", icon: Scale },
  { key: "visibility", label: "Feature Access", icon: Eye },
  { key: "checklists", label: "Checklists", icon: ClipboardList },
  { key: "settings", label: "Settings", icon: SettingsIcon },
  { key: "audit", label: "Audit Log", icon: ScrollText },
];

export default function Admin() {
  const [tab, setTab] = useState("users");
  return (
    <div className="space-y-6" data-testid="admin-root">
      <div>
        <div className="label-caps">Administration</div>
        <h1 className="font-heading text-3xl sm:text-4xl">Admin Center</h1>
      </div>

      <div className="flex flex-wrap gap-2 border-b border-[color:var(--border)]">
        {TABS.map((t) => {
          const Icon = t.icon;
          return (
            <button key={t.key} onClick={() => setTab(t.key)} data-testid={`admin-tab-${t.key}`}
                    className={`flex items-center gap-2 px-4 py-3 border-b-2 -mb-[1px] ${tab === t.key ? "border-[color:var(--gold)] text-[color:var(--gold)]" : "border-transparent text-[color:var(--text-secondary)]"}`}>
              <Icon size={16}/> {t.label}
            </button>
          );
        })}
      </div>

      {tab === "users" && <UsersTab />}
      {tab === "stores" && <StoresTab />}
      {tab === "registers" && <RegistersTab />}
      {tab === "suppliers" && <SuppliersTab />}
      {tab === "display_areas" && <DisplayAreasTab />}
      {tab === "zones" && <ZonesTab />}
      {tab === "fandoms" && <FandomsTab />}
      {tab === "standards" && <DisplayStandardsTab />}
      {tab === "visibility" && <VisibilityTab />}
      {tab === "checklists" && <ChecklistsTab />}
      {tab === "settings" && <SettingsTab />}
      {tab === "audit" && <AuditTab />}
    </div>
  );
}

function VisibilityTab() {
  const [vis, setVis] = useState(null);
  const load = useCallback(() => api.get("/feature_visibility").then(({ data }) => setVis(data.visibility)), []);
  useEffect(() => { load(); }, [load]);
  const set = (feature, role, val) => setVis((prev) => ({ ...prev, [feature]: { ...(prev[feature] || {}), [role]: val } }));
  const save = async () => {
    try {
      await api.put("/feature_visibility", { visibility: vis });
      toast.success("Feature access updated");
      const { refreshVisibility } = await import("@/lib/visibility");
      await refreshVisibility();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  if (!vis) return <div className="text-[color:var(--text-secondary)]">Loading…</div>;
  const FEATS = [
    { key: "schedule", label: "Schedule Tab" },
    { key: "template", label: "Template Tab" },
    { key: "weekly", label: "Weekly Tab" },
  ];
  return (
    <div className="space-y-4">
      <div className="hos-card p-4">
        <div className="font-heading text-xl mb-1">Manager &amp; Employee Tab Visibility</div>
        <div className="text-sm text-[color:var(--text-secondary)] mb-4">Hidden features are completely removed from that role's navigation, and their API endpoints return 403 for the affected roles. Admin and GM always retain access.</div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left label-caps"><tr><th className="p-2">Feature</th><th>Manager</th><th>Employee</th></tr></thead>
            <tbody>
              {FEATS.map((f) => (
                <tr key={f.key} className="border-t border-[color:var(--border)]">
                  <td className="p-3 font-semibold">{f.label}</td>
                  {["manager", "employee"].map((r) => (
                    <td key={r}>
                      <label className="inline-flex items-center gap-2 cursor-pointer" data-testid={`vis-${f.key}-${r}`}>
                        <input type="checkbox" checked={!!vis[f.key]?.[r]} onChange={(e) => set(f.key, r, e.target.checked)}/>
                        <span className="text-xs">{vis[f.key]?.[r] ? "ON" : "OFF"}</span>
                      </label>
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <button className="hos-btn-primary flex items-center gap-2 mt-4" onClick={save} data-testid="save-visibility"><Save size={16}/> Save Visibility</button>
      </div>
    </div>
  );
}

function SuppliersTab() {
  const [items, setItems] = useState([]);
  const [f, setF] = useState({ name: "", code: "", contact_name: "", phone: "", email: "", address: "", notes: "", active: true });
  const load = useCallback(async () => {
    const r = await api.get("/suppliers"); setItems(r.data);
  }, []);
  useEffect(() => { load(); }, [load]);
  const create = async () => {
    if (!f.name.trim()) return toast.error("Supplier name required");
    try { await api.post("/suppliers", f); toast.success("Supplier added"); setF({ name: "", code: "", contact_name: "", phone: "", email: "", address: "", notes: "", active: true }); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const update = async (id, patch) => {
    try { await api.patch(`/suppliers/${id}`, patch); load(); } catch (e) { toast.error(formatApiError(e)); }
  };
  const remove = async (s) => {
    if (!window.confirm(`Remove ${s.name}? Suppliers with receiving history will be deactivated instead of deleted.`)) return;
    try { await api.delete(`/suppliers/${s.id}`); toast.success("Removed"); load(); } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <div className="space-y-4">
      <div className="hos-card p-4 grid grid-cols-1 md:grid-cols-3 gap-3">
        <input className="hos-input" placeholder="Supplier Name *" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="sup-name"/>
        <input className="hos-input" placeholder="Supplier Code" value={f.code} onChange={(e) => setF({ ...f, code: e.target.value })}/>
        <input className="hos-input" placeholder="Contact Name" value={f.contact_name} onChange={(e) => setF({ ...f, contact_name: e.target.value })}/>
        <input className="hos-input" placeholder="Phone" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })}/>
        <input className="hos-input" placeholder="Email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })}/>
        <input className="hos-input" placeholder="Address" value={f.address} onChange={(e) => setF({ ...f, address: e.target.value })}/>
        <input className="hos-input md:col-span-3" placeholder="Notes" value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })}/>
        <div className="md:col-span-3 flex justify-end">
          <button className="hos-btn-primary flex items-center gap-2" onClick={create} data-testid="admin-add-supplier"><Plus size={16}/> Add Supplier</button>
        </div>
      </div>
      <div className="hos-card p-4">
        <table className="w-full text-sm">
          <thead className="text-left label-caps"><tr><th className="p-2">Name</th><th>Code</th><th>Contact</th><th>Phone</th><th>Email</th><th>Active</th><th></th></tr></thead>
          <tbody>
            {items.map((r) => (
              <tr key={r.id} className="border-t border-[color:var(--border)]">
                <td className="py-2"><input className="hos-input py-1" defaultValue={r.name} onBlur={(e) => update(r.id, { ...r, name: e.target.value })}/></td>
                <td><input className="hos-input py-1 max-w-[100px]" defaultValue={r.code || ""} onBlur={(e) => update(r.id, { ...r, code: e.target.value })}/></td>
                <td><input className="hos-input py-1" defaultValue={r.contact_name || ""} onBlur={(e) => update(r.id, { ...r, contact_name: e.target.value })}/></td>
                <td><input className="hos-input py-1" defaultValue={r.phone || ""} onBlur={(e) => update(r.id, { ...r, phone: e.target.value })}/></td>
                <td><input className="hos-input py-1" defaultValue={r.email || ""} onBlur={(e) => update(r.id, { ...r, email: e.target.value })}/></td>
                <td><input type="checkbox" checked={!!r.active} onChange={(e) => update(r.id, { ...r, active: e.target.checked })}/></td>
                <td><button onClick={() => remove(r)} className="text-[color:var(--error)] p-2"><Trash2 size={14}/></button></td>
              </tr>
            ))}
          </tbody>
        </table>
        {items.length === 0 && <div className="text-center text-[color:var(--text-secondary)] py-4">No suppliers yet.</div>}
      </div>
    </div>
  );
}

function DisplayAreasTab() {
  const [zones, setZones] = useState([]);
  const [types, setTypes] = useState([]);
  const [items, setItems] = useState([]);
  const [f, setF] = useState({ zone_id: "", display_area_type: "Aisle", code: "", name: "", description: "", sort_order: 0, active: true });
  const [selectedZone, setSelectedZone] = useState("");
  const load = useCallback(async () => {
    const [z, t] = await Promise.all([api.get("/zones"), api.get("/display_area_types")]);
    setZones(z.data); setTypes(t.data);
    const params = selectedZone ? `?zone_id=${selectedZone}` : "";
    const r = await api.get(`/display_areas${params}`);
    setItems(r.data);
  }, [selectedZone]);
  useEffect(() => { load(); }, [load]);
  const create = async () => {
    if (!f.zone_id) return toast.error("Select a zone");
    if (!f.code.trim()) return toast.error("Code required");
    try { await api.post("/display_areas", f); toast.success("Display Area added"); setF({ ...f, code: "", name: "", description: "" }); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const update = async (id, patch) => {
    try { await api.patch(`/display_areas/${id}`, patch); load(); } catch (e) { toast.error(formatApiError(e)); }
  };
  const remove = async (id) => {
    if (!window.confirm("Deactivate this Display Area?")) return;
    try { await api.delete(`/display_areas/${id}`); toast.success("Deactivated"); load(); } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <div className="space-y-4">
      <div className="hos-card p-4 grid grid-cols-1 md:grid-cols-6 gap-3 items-end">
        <div>
          <label className="label-caps">Zone *</label>
          <select className="hos-input mt-1" value={f.zone_id} onChange={(e) => setF({ ...f, zone_id: e.target.value })} data-testid="da-zone-select">
            <option value="">— zone —</option>
            {zones.map((z) => <option key={z.id} value={z.id}>{z.name}</option>)}
          </select>
        </div>
        <div>
          <label className="label-caps">Type *</label>
          <select className="hos-input mt-1" value={f.display_area_type} onChange={(e) => setF({ ...f, display_area_type: e.target.value })}>
            {types.map((t) => <option key={t.id} value={t.name}>{t.name}</option>)}
          </select>
        </div>
        <div>
          <label className="label-caps">Code *</label>
          <input className="hos-input mt-1" placeholder="A1 / P2 / T1 / G3" value={f.code} onChange={(e) => setF({ ...f, code: e.target.value.toUpperCase() })} data-testid="da-code-input"/>
        </div>
        <div className="md:col-span-2">
          <label className="label-caps">Name</label>
          <input className="hos-input mt-1" placeholder="Wand Aisle" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })}/>
        </div>
        <button className="hos-btn-primary flex items-center gap-2" onClick={create} data-testid="da-add-btn"><Plus size={16}/> Add</button>
      </div>
      <div className="hos-card p-4">
        <div className="flex items-center gap-3 mb-3">
          <label className="label-caps">Filter by Zone</label>
          <select className="hos-input max-w-xs" value={selectedZone} onChange={(e) => setSelectedZone(e.target.value)}>
            <option value="">All zones</option>
            {zones.map((z) => <option key={z.id} value={z.id}>{z.name}</option>)}
          </select>
        </div>
        <table className="w-full text-sm">
          <thead className="text-left label-caps"><tr><th className="p-2">Zone</th><th>Type</th><th>Code</th><th>Name</th><th>Order</th><th>Active</th><th></th></tr></thead>
          <tbody>
            {items.map((r) => (
              <tr key={r.id} className="border-t border-[color:var(--border)]">
                <td className="py-2">{r.zone_name}</td>
                <td><input className="hos-input py-1" defaultValue={r.display_area_type} onBlur={(e) => update(r.id, { ...r, display_area_type: e.target.value })}/></td>
                <td><input className="hos-input py-1 max-w-[110px] uppercase" defaultValue={r.code} onBlur={(e) => update(r.id, { ...r, code: e.target.value.toUpperCase() })}/></td>
                <td><input className="hos-input py-1" defaultValue={r.name} onBlur={(e) => update(r.id, { ...r, name: e.target.value })}/></td>
                <td><input type="number" className="hos-input py-1 max-w-[70px]" defaultValue={r.sort_order || 0} onBlur={(e) => update(r.id, { ...r, sort_order: Number(e.target.value) || 0 })}/></td>
                <td><input type="checkbox" checked={!!r.active} onChange={(e) => update(r.id, { ...r, active: e.target.checked })}/></td>
                <td><button onClick={() => remove(r.id)} className="text-[color:var(--error)] p-2"><Trash2 size={14}/></button></td>
              </tr>
            ))}
          </tbody>
        </table>
        {items.length === 0 && <div className="text-center text-[color:var(--text-secondary)] py-4">No display areas yet. Add A1, P1, T1, G1 to get started.</div>}
      </div>
    </div>
  );
}

function RegistersTab() {
  const [items, setItems] = useState([]);
  const [stores, setStores] = useState([]);
  const [f, setF] = useState({ store_id: "", name: "", register_number: "", location: "", pos_terminal_id: "", active: true });
  const load = useCallback(async () => {
    const [r, s] = await Promise.all([api.get("/registers"), api.get("/stores")]);
    setItems(r.data); setStores(s.data);
    if (!f.store_id && s.data[0]) setF((p) => ({ ...p, store_id: s.data[0].id }));
  }, []); // eslint-disable-line
  useEffect(() => { load(); }, [load]);
  const create = async () => {
    if (!f.store_id) return toast.error("Pick a store");
    if (!f.name.trim()) return toast.error("Register name required");
    try { await api.post("/registers", f); toast.success("Register added"); setF({ ...f, name: "", register_number: "", location: "", pos_terminal_id: "" }); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const update = async (id, patch) => {
    try { await api.patch(`/registers/${id}`, patch); load(); } catch (e) { toast.error(formatApiError(e)); }
  };
  const deactivate = async (r) => {
    if (!window.confirm(`Deactivate ${r.name}? History will be preserved.`)) return;
    try { await api.delete(`/registers/${r.id}`); toast.success("Deactivated"); load(); } catch (e) { toast.error(formatApiError(e)); }
  };
  const storeName = (id) => stores.find((s) => s.id === id)?.name || "?";
  const byStore = items.reduce((m, r) => { (m[r.store_id] = m[r.store_id] || []).push(r); return m; }, {});
  return (
    <div className="space-y-4">
      <div className="hos-card p-4 grid grid-cols-1 md:grid-cols-3 gap-3">
        <select className="hos-input" value={f.store_id} onChange={(e) => setF({ ...f, store_id: e.target.value })}>
          <option value="">— select store —</option>
          {stores.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
        <input className="hos-input" placeholder="Register name (e.g. Register 4)" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="reg-name"/>
        <input className="hos-input" placeholder="Number (e.g. R04)" value={f.register_number} onChange={(e) => setF({ ...f, register_number: e.target.value })} data-testid="reg-number"/>
        <input className="hos-input md:col-span-2" placeholder="Location / Floor (e.g. Basement)" value={f.location} onChange={(e) => setF({ ...f, location: e.target.value })} data-testid="reg-location"/>
        <input className="hos-input" placeholder="POS Terminal ID (optional)" value={f.pos_terminal_id} onChange={(e) => setF({ ...f, pos_terminal_id: e.target.value })}/>
        <div className="md:col-span-3 flex justify-end">
          <button className="hos-btn-primary flex items-center gap-2" onClick={create} data-testid="admin-add-register"><Plus size={16}/> Add Register</button>
        </div>
      </div>
      {Object.entries(byStore).map(([sid, regs]) => (
        <div key={sid} className="hos-card p-4">
          <div className="font-heading text-lg mb-2">{storeName(sid)}</div>
          <table className="w-full text-sm">
            <thead className="text-left label-caps"><tr><th>Name</th><th>Number</th><th>Location</th><th>POS ID</th><th>Active</th><th></th></tr></thead>
            <tbody>
              {regs.map((r) => (
                <tr key={r.id} className="border-t border-[color:var(--border)]">
                  <td className="py-2"><input className="hos-input py-1" defaultValue={r.name} onBlur={(e) => update(r.id, { ...r, name: e.target.value })}/></td>
                  <td><input className="hos-input py-1 max-w-[100px]" defaultValue={r.register_number || ""} onBlur={(e) => update(r.id, { ...r, register_number: e.target.value })}/></td>
                  <td><input className="hos-input py-1" defaultValue={r.location || ""} onBlur={(e) => update(r.id, { ...r, location: e.target.value })}/></td>
                  <td><input className="hos-input py-1 max-w-[120px]" defaultValue={r.pos_terminal_id || ""} onBlur={(e) => update(r.id, { ...r, pos_terminal_id: e.target.value })}/></td>
                  <td><input type="checkbox" checked={!!r.active} onChange={(e) => update(r.id, { ...r, active: e.target.checked })}/></td>
                  <td><button onClick={() => deactivate(r)} className="text-[color:var(--error)] p-2"><Trash2 size={14}/></button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}
      {items.length === 0 && <div className="hos-card p-6 text-center text-[color:var(--text-secondary)]">No registers yet.</div>}
    </div>
  );
}

function StoresTab() {
  const [items, setItems] = useState([]);
  const [f, setF] = useState({ name: "", address: "", active: true });
  const load = useCallback(() => api.get("/stores").then(({ data }) => setItems(data)), []);
  useEffect(() => { load(); }, [load]);
  const create = async () => {
    if (!f.name.trim()) return toast.error("Name required");
    try { await api.post("/stores", f); toast.success("Store added"); setF({ name: "", address: "", active: true }); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const update = async (id, patch) => {
    try { await api.patch(`/stores/${id}`, patch); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const remove = async (s) => {
    if (!window.confirm(`Delete store "${s.name}"?`)) return;
    try { await api.delete(`/stores/${s.id}`); load(); } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <div className="space-y-4">
      <div className="hos-card p-4 grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="admin-store-form">
        <input className="hos-input" placeholder="Store name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })}/>
        <input className="hos-input" placeholder="Address" value={f.address} onChange={(e) => setF({ ...f, address: e.target.value })}/>
        <button className="hos-btn-primary flex items-center gap-2 justify-center" onClick={create} data-testid="admin-add-store"><Plus size={16}/> Add Store</button>
      </div>
      <div className="hos-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-left label-caps"><tr><th className="p-3">Name</th><th>Address</th><th>Active</th><th></th></tr></thead>
          <tbody>
            {items.map((s) => (
              <tr key={s.id} className="border-t border-[color:var(--border)]">
                <td className="p-3"><input className="hos-input py-1" defaultValue={s.name} onBlur={(e) => update(s.id, { ...s, name: e.target.value })}/></td>
                <td><input className="hos-input py-1" defaultValue={s.address || ""} onBlur={(e) => update(s.id, { ...s, address: e.target.value })}/></td>
                <td><input type="checkbox" checked={!!s.active} onChange={(e) => update(s.id, { ...s, active: e.target.checked })}/></td>
                <td><button onClick={() => remove(s)} className="text-[color:var(--error)] p-2"><Trash2 size={14}/></button></td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={4} className="p-6 text-center text-[color:var(--text-secondary)]">No stores yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function FandomsTab() {
  const [items, setItems] = useState([]);
  const [f, setF] = useState({ name: "", code: "", description: "", active: true });
  const load = useCallback(() => api.get("/fandoms").then(({ data }) => setItems(data)), []);
  useEffect(() => { load(); }, [load]);
  const create = async () => {
    if (!f.name.trim()) return toast.error("Name required");
    try { await api.post("/fandoms", f); toast.success("Fandom added"); setF({ name: "", code: "", description: "", active: true }); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const update = async (id, patch) => {
    try { await api.patch(`/fandoms/${id}`, patch); load(); } catch (e) { toast.error(formatApiError(e)); }
  };
  const remove = async (x) => {
    if (!window.confirm(`Delete "${x.name}"?`)) return;
    try { await api.delete(`/fandoms/${x.id}`); load(); } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <div className="space-y-4">
      <div className="hos-card p-4 grid grid-cols-1 md:grid-cols-4 gap-3">
        <input className="hos-input" placeholder="Fandom name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })}/>
        <input className="hos-input" placeholder="Code (optional)" value={f.code} onChange={(e) => setF({ ...f, code: e.target.value })}/>
        <input className="hos-input" placeholder="Description" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })}/>
        <button className="hos-btn-primary flex items-center gap-2 justify-center" onClick={create} data-testid="admin-add-fandom"><Plus size={16}/> Add Fandom</button>
      </div>
      <div className="hos-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-left label-caps"><tr><th className="p-3">Name</th><th>Code</th><th>Description</th><th>Active</th><th></th></tr></thead>
          <tbody>
            {items.map((x) => (
              <tr key={x.id} className="border-t border-[color:var(--border)]">
                <td className="p-3"><input className="hos-input py-1" defaultValue={x.name} onBlur={(e) => update(x.id, { ...x, name: e.target.value })}/></td>
                <td><input className="hos-input py-1" defaultValue={x.code || ""} onBlur={(e) => update(x.id, { ...x, code: e.target.value })}/></td>
                <td><input className="hos-input py-1" defaultValue={x.description || ""} onBlur={(e) => update(x.id, { ...x, description: e.target.value })}/></td>
                <td><input type="checkbox" checked={!!x.active} onChange={(e) => update(x.id, { ...x, active: e.target.checked })}/></td>
                <td><button onClick={() => remove(x)} className="text-[color:var(--error)] p-2"><Trash2 size={14}/></button></td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={5} className="p-6 text-center text-[color:var(--text-secondary)]">No fandoms yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ZonesTab() {
  const [items, setItems] = useState([]);
  const [stores, setStores] = useState([]);
  const [fandoms, setFandoms] = useState([]);
  const [f, setF] = useState({ store_id: "", name: "", code: "", floor: "", description: "", fandom_ids: [], active: true });
  const load = useCallback(async () => {
    const [z, s, fa] = await Promise.all([api.get("/zones"), api.get("/stores"), api.get("/fandoms")]);
    setItems(z.data); setStores(s.data); setFandoms(fa.data);
    if (!f.store_id && s.data[0]) setF((prev) => ({ ...prev, store_id: s.data[0].id }));
  }, []); // eslint-disable-line
  useEffect(() => { load(); }, [load]);
  const create = async () => {
    if (!f.store_id) return toast.error("Pick a store");
    if (!f.name.trim()) return toast.error("Name required");
    try { await api.post("/zones", f); toast.success("Zone added"); setF({ ...f, name: "", code: "", floor: "", description: "", fandom_ids: [] }); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const update = async (id, patch) => {
    try { await api.patch(`/zones/${id}`, patch); load(); } catch (e) { toast.error(formatApiError(e)); }
  };
  const remove = async (z) => {
    if (!window.confirm(`Delete zone "${z.name}"?`)) return;
    try { await api.delete(`/zones/${z.id}`); load(); } catch (e) { toast.error(formatApiError(e)); }
  };
  const toggleFandom = (id) => setF((prev) => ({ ...prev, fandom_ids: prev.fandom_ids.includes(id) ? prev.fandom_ids.filter((x) => x !== id) : [...prev.fandom_ids, id] }));
  const storeName = (id) => stores.find((s) => s.id === id)?.name || "?";
  return (
    <div className="space-y-4">
      <div className="hos-card p-4 space-y-3">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <select className="hos-input" value={f.store_id} onChange={(e) => setF({ ...f, store_id: e.target.value })}>
            <option value="">— select store —</option>
            {stores.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
          <input className="hos-input" placeholder="Zone name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })}/>
          <input className="hos-input" placeholder="Code" value={f.code} onChange={(e) => setF({ ...f, code: e.target.value })}/>
          <input className="hos-input" placeholder="Floor" value={f.floor} onChange={(e) => setF({ ...f, floor: e.target.value })}/>
          <input className="hos-input md:col-span-2" placeholder="Description" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })}/>
        </div>
        <div>
          <label className="label-caps">Associated Fandoms</label>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-1 mt-1">
            {fandoms.map((fa) => (
              <label key={fa.id} className="flex items-center gap-2 text-xs p-1">
                <input type="checkbox" checked={f.fandom_ids.includes(fa.id)} onChange={() => toggleFandom(fa.id)}/>
                {fa.name}
              </label>
            ))}
          </div>
        </div>
        <button className="hos-btn-primary flex items-center gap-2" onClick={create} data-testid="admin-add-zone"><Plus size={16}/> Add Zone</button>
      </div>
      <div className="hos-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-left label-caps"><tr><th className="p-3">Name</th><th>Store</th><th>Floor</th><th>Fandoms</th><th>Active</th><th></th></tr></thead>
          <tbody>
            {items.map((z) => (
              <tr key={z.id} className="border-t border-[color:var(--border)]">
                <td className="p-3"><input className="hos-input py-1" defaultValue={z.name} onBlur={(e) => update(z.id, { ...z, name: e.target.value })}/></td>
                <td className="text-xs">{storeName(z.store_id)}</td>
                <td><input className="hos-input py-1 max-w-[100px]" defaultValue={z.floor || ""} onBlur={(e) => update(z.id, { ...z, floor: e.target.value })}/></td>
                <td className="text-xs">{(z.fandom_ids || []).map((id) => fandoms.find((f) => f.id === id)?.name).filter(Boolean).join(", ") || "—"}</td>
                <td><input type="checkbox" checked={!!z.active} onChange={(e) => update(z.id, { ...z, active: e.target.checked })}/></td>
                <td><button onClick={() => remove(z)} className="text-[color:var(--error)] p-2"><Trash2 size={14}/></button></td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={6} className="p-6 text-center text-[color:var(--text-secondary)]">No zones yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function UsersTab() {
  const [users, setUsers] = useState([]);
  const [creating, setCreating] = useState(false);
  const [f, setF] = useState({ email: "", password: "", name: "", role: "employee", employee_id: "", phone: "" });
  const load = useCallback(() => api.get("/users").then(({ data }) => setUsers(data)), []);
  useEffect(() => { load(); }, [load]);
  const create = async () => {
    try { await api.post("/users", f); toast.success("User created"); setCreating(false); setF({ email: "", password: "", name: "", role: "employee", employee_id: "", phone: "" }); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const toggle = async (u) => {
    try { await api.patch(`/users/${u.id}`, { active: !u.active }); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const changeRole = async (u, role) => {
    try { await api.patch(`/users/${u.id}`, { role }); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const savePhone = async (u, phone) => {
    if (phone === (u.phone || "")) return;
    try { await api.patch(`/users/${u.id}`, { phone }); toast.success("Phone saved"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const { user: me } = useAuth();
  const resetPassword = async (u) => {
    const pw = window.prompt(`Set a new password for ${u.name}:\n(minimum 6 characters — the user should change it after logging in)`);
    if (pw === null) return;
    if (pw.length < 6) return toast.error("Password must be at least 6 characters");
    try { await api.patch(`/users/${u.id}`, { password: pw }); toast.success("Password reset"); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const removeUser = async (u) => {
    if (me?.id === u.id) return toast.error("You cannot delete your own account");
    if (!window.confirm(`Permanently delete "${u.name}" (${u.email})?\n\nThis cannot be undone. Historical records (schedules, reports, audit logs) will be preserved.`)) return;
    if (!window.confirm(`Are you absolutely sure? Type OK on the next dialog.`)) return;
    const confirm2 = window.prompt(`To confirm, type DELETE`);
    if (confirm2 !== "DELETE") return toast.error("Deletion cancelled");
    try { await api.delete(`/users/${u.id}`); toast.success("User deleted"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <div className="space-y-4">
      <button className="hos-btn-primary flex items-center gap-2" onClick={() => setCreating(!creating)} data-testid="admin-new-user"><Plus size={16}/> New User</button>
      {creating && (
        <div className="hos-card p-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
          <input className="hos-input" placeholder="Name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })}/>
          <input className="hos-input" placeholder="Email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })}/>
          <input className="hos-input" type="password" placeholder="Password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })}/>
          <input className="hos-input" placeholder="Employee ID" value={f.employee_id} onChange={(e) => setF({ ...f, employee_id: e.target.value })}/>
          <input className="hos-input" placeholder="Phone (E.164 e.g. +14155551212)" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} data-testid="admin-new-user-phone"/>
          <select className="hos-input" value={f.role} onChange={(e) => setF({ ...f, role: e.target.value })} data-testid="admin-new-user-role">
            {ROLE_OPTIONS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
          </select>
          <button className="hos-btn-primary sm:col-span-2" onClick={create} data-testid="admin-create-user-btn">Create</button>
        </div>
      )}
      <div className="hos-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-left label-caps"><tr><th className="p-3">Name</th><th>Email</th><th>Emp ID</th><th>Phone</th><th>Role</th><th>Active</th><th></th></tr></thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className="border-t border-[color:var(--border)]">
                <td className="p-3">{u.name}</td>
                <td>{u.email}</td>
                <td>{u.employee_id || "—"}</td>
                <td>
                  <input
                    className="hos-input py-1 min-w-[160px]"
                    defaultValue={u.phone || ""}
                    placeholder="+14155551212"
                    onBlur={(e) => savePhone(u, e.target.value.trim())}
                    data-testid={`user-phone-${u.id}`}
                  />
                </td>
                <td>
                  <select className="hos-input py-1" value={u.role} onChange={(e) => changeRole(u, e.target.value)} data-testid={`user-role-${u.id}`}>
                    {ROLE_OPTIONS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
                  </select>
                </td>
                <td>{u.active ? <span className="status-pill status-open">active</span> : <span className="status-pill status-neutral">off</span>}</td>
                <td>
                  <div className="flex items-center gap-2 flex-wrap">
                    <button className="hos-btn-outline py-1 px-3" onClick={() => toggle(u)} data-testid={`user-toggle-${u.id}`}>{u.active ? "Deactivate" : "Activate"}</button>
                    <button className="hos-btn-outline py-1 px-2 flex items-center gap-1" onClick={() => resetPassword(u)} data-testid={`user-reset-pw-${u.id}`} title="Reset password">
                      <KeyRound size={14}/> Reset PW
                    </button>
                    <button className="py-1 px-2 rounded-md text-[color:var(--error)] border border-[color:var(--error)]/40 hover:bg-[color:var(--error)]/10 flex items-center gap-1 text-sm" onClick={() => removeUser(u)} data-testid={`user-delete-${u.id}`} disabled={me?.id === u.id} title={me?.id === u.id ? "You cannot delete yourself" : "Delete user"}>
                      <Trash2 size={14}/> Delete
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ChecklistsTab() {
  const [type, setType] = useState("opening");
  const [items, setItems] = useState([]);
  const load = useCallback(() => api.get(`/checklists/${type}`).then(({ data }) => setItems(data?.items || [])), [type]);
  useEffect(() => { load(); }, [load]);
  const save = async () => {
    try { await api.put(`/checklists/${type}`, { items }); toast.success("Checklist saved"); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const addItem = () => setItems([...items, { id: `${type}-new-${Date.now()}`, order: items.length + 1, title: "New task", required: true, section: type === "closing" ? "Security & Cash" : "Store Opening Tasks" }]);
  return (
    <div className="space-y-4">
      <div className="flex gap-3 items-center">
        <select className="hos-input max-w-[200px]" value={type} onChange={(e) => setType(e.target.value)}>
          <option value="opening">Opening</option><option value="closing">Closing</option>
        </select>
        <button className="hos-btn-outline flex items-center gap-2" onClick={addItem}><Plus size={16}/> Add Task</button>
        <button className="hos-btn-primary flex items-center gap-2" onClick={save} data-testid="admin-save-checklist"><Save size={16}/> Save</button>
      </div>
      <div className="space-y-2">
        {items.map((it, idx) => (
          <div key={it.id} className="hos-card p-3 flex flex-wrap gap-2 items-center">
            <span className="w-8 text-center text-[color:var(--gold)]">{idx + 1}</span>
            <input className="hos-input flex-1 min-w-[240px]" value={it.title} onChange={(e) => setItems(items.map((x, i) => i === idx ? { ...x, title: e.target.value } : x))}/>
            <input className="hos-input max-w-[200px]" value={it.section || ""} onChange={(e) => setItems(items.map((x, i) => i === idx ? { ...x, section: e.target.value } : x))}/>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={!!it.required} onChange={(e) => setItems(items.map((x, i) => i === idx ? { ...x, required: e.target.checked } : x))}/> Required
            </label>
            <button className="text-[color:var(--error)] px-2" onClick={() => setItems(items.filter((_, i) => i !== idx))}>Remove</button>
          </div>
        ))}
      </div>
    </div>
  );
}

function SettingsTab() {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/settings").then(({ data }) => setS(data)); }, []);
  if (!s) return <div className="text-[color:var(--text-secondary)]">Loading…</div>;
  const save = async () => {
    try { await api.put("/settings", s); toast.success("Settings saved"); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const days = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];
  return (
    <div className="hos-card p-6 space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="label-caps">Store Name</label>
          <input className="hos-input" value={s.store_name} onChange={(e) => setS({ ...s, store_name: e.target.value })}/>
        </div>
        <div>
          <label className="label-caps">Store Address</label>
          <input className="hos-input" value={s.store_address} onChange={(e) => setS({ ...s, store_address: e.target.value })}/>
        </div>
        <div>
          <label className="label-caps">Opening early (min)</label>
          <input type="number" className="hos-input" value={s.opening_early_minutes || 0} onChange={(e) => setS({ ...s, opening_early_minutes: Number(e.target.value) })}/>
        </div>
        <div>
          <label className="label-caps">Closing late (min)</label>
          <input type="number" className="hos-input" value={s.closing_late_minutes || 0} onChange={(e) => setS({ ...s, closing_late_minutes: Number(e.target.value) })}/>
        </div>
      </div>
      <div>
        <div className="label-caps mb-2">Schedule</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          {days.map((d) => (
            <div key={d} className="flex items-center gap-2">
              <span className="w-12 uppercase text-[color:var(--text-secondary)]">{d}</span>
              <input className="hos-input py-2" value={s.schedule?.[d]?.open || ""} onChange={(e) => setS({ ...s, schedule: { ...(s.schedule || {}), [d]: { ...((s.schedule || {})[d] || {}), open: e.target.value } } })}/>
              <input className="hos-input py-2" value={s.schedule?.[d]?.close || ""} onChange={(e) => setS({ ...s, schedule: { ...(s.schedule || {}), [d]: { ...((s.schedule || {})[d] || {}), close: e.target.value } } })}/>
            </div>
          ))}
        </div>
      </div>
      <div>
        <div className="label-caps mb-2">Notification recipients (comma-separated emails)</div>
        <input className="hos-input" value={(s.notification_recipients || []).join(", ")} onChange={(e) => setS({ ...s, notification_recipients: e.target.value.split(",").map((x) => x.trim()).filter(Boolean) })}/>
      </div>
      <label className="flex items-start gap-3 p-3 rounded-lg border border-[color:var(--border)]">
        <input type="checkbox" checked={!!s.allow_employees_full_schedule} onChange={(e) => setS({ ...s, allow_employees_full_schedule: e.target.checked })} data-testid="admin-allow-full-schedule"/>
        <div>
          <div className="font-semibold">Allow Employees to View Full Team Schedule</div>
          <div className="text-xs text-[color:var(--text-secondary)] mt-1">When enabled, employees can view every shift on a published weekly schedule (not only their own).</div>
        </div>
      </label>
      <div>
        <label className="label-caps">Minimum photos per zone (Display Check)</label>
        <input type="number" min="1" max="10" className="hos-input max-w-[120px]" value={s.min_photos_per_zone || 1} onChange={(e) => setS({ ...s, min_photos_per_zone: Number(e.target.value) })} data-testid="admin-min-photos"/>
        <div className="text-xs text-[color:var(--text-secondary)] mt-1">Managers must upload at least this many main photos per zone.</div>
      </div>
      <div>
        <label className="label-caps">Maximum photos per zone / per issue</label>
        <input type="number" min="5" max="100" className="hos-input max-w-[120px]" value={s.max_photos_per_zone || 30} onChange={(e) => setS({ ...s, max_photos_per_zone: Number(e.target.value) })} data-testid="admin-max-photos"/>
        <div className="text-xs text-[color:var(--text-secondary)] mt-1">Infrastructure cap on each photo gallery (main and per-issue). Default 30.</div>
      </div>
      <label className="flex items-start gap-3 p-3 rounded-lg border border-[color:var(--border)]">
        <input type="checkbox" checked={s.require_zone_owner !== false} onChange={(e) => setS({ ...s, require_zone_owner: e.target.checked })} data-testid="admin-require-zone-owner"/>
        <div>
          <div className="font-semibold">Require Zone Owner selection each inspection</div>
          <div className="text-xs text-[color:var(--text-secondary)] mt-1">Managers must select at least one Zone Owner (daily / per-inspection) before completing a Zone. Zone Owners are never permanently linked to a Zone.</div>
        </div>
      </label>
      <button className="hos-btn-primary flex items-center gap-2" onClick={save} data-testid="admin-save-settings"><Save size={16}/> Save Settings</button>
    </div>
  );
}

function AuditTab() {
  const [logs, setLogs] = useState([]);
  useEffect(() => { api.get("/audit").then(({ data }) => setLogs(data)); }, []);
  return (
    <div className="hos-card overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="text-left label-caps"><tr><th className="p-3">When</th><th>Action</th><th>User</th><th>Details</th></tr></thead>
        <tbody>
          {logs.map((l) => (
            <tr key={l.id} className="border-t border-[color:var(--border)]">
              <td className="p-3">{new Date(l.timestamp).toLocaleString()}</td>
              <td className="text-[color:var(--gold)]">{l.action}</td>
              <td>{l.user_name}</td>
              <td className="text-xs text-[color:var(--text-secondary)]">{JSON.stringify(l.details)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}


function DisplayStandardsTab() {
  const [items, setItems] = useState([]);
  const [zones, setZones] = useState([]);
  const [fandoms, setFandoms] = useState([]);
  const [form, setForm] = useState({ name: "", description: "", zone_id: "", fandom_id: "", weight_percentage: 0, scoring_type: "manual", active: true, sort_order: 0 });
  const load = useCallback(async () => {
    const [s, z, f] = await Promise.all([api.get("/display_standards"), api.get("/zones"), api.get("/fandoms")]);
    setItems(s.data); setZones(z.data); setFandoms(f.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!form.name.trim() || !form.zone_id) return toast.error("Name and Zone required");
    try {
      await api.post("/display_standards", { ...form, fandom_id: form.fandom_id || null, weight_percentage: Number(form.weight_percentage) });
      toast.success("Standard added"); setForm({ ...form, name: "", description: "", weight_percentage: 0, sort_order: 0 }); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const update = async (id, patch) => { try { await api.patch(`/display_standards/${id}`, patch); load(); } catch (e) { toast.error(formatApiError(e)); } };
  const del = async (id) => { if (!window.confirm("Delete this standard?")) return; try { await api.delete(`/display_standards/${id}`); load(); } catch (e) { toast.error(formatApiError(e)); } };

  // Group by (zone, fandom) and compute weight totals per group
  const groups = {};
  for (const s of items) {
    const k = `${s.zone_id}::${s.fandom_id || ""}`;
    (groups[k] ||= { zone_id: s.zone_id, fandom_id: s.fandom_id, items: [], total: 0 });
    groups[k].items.push(s);
    if (s.active !== false) groups[k].total += Number(s.weight_percentage || 0);
  }
  const zName = (id) => zones.find((z) => z.id === id)?.name || id;
  const fName = (id) => (id ? fandoms.find((f) => f.id === id)?.name : "All fandoms") || id;

  return (
    <div className="space-y-4" data-testid="admin-standards">
      <div className="hos-card p-4">
        <div className="font-heading text-xl mb-3">Add Display Standard</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <input className="hos-input" placeholder="Name (e.g. Product Placement)" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="std-name"/>
          <input className="hos-input" placeholder="Description (optional)" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}/>
          <select className="hos-input" value={form.zone_id} onChange={(e) => setForm({ ...form, zone_id: e.target.value })} data-testid="std-zone">
            <option value="">Select Zone…</option>
            {zones.map((z) => <option key={z.id} value={z.id}>{z.name}</option>)}
          </select>
          <select className="hos-input" value={form.fandom_id} onChange={(e) => setForm({ ...form, fandom_id: e.target.value })} data-testid="std-fandom">
            <option value="">All fandoms in this zone</option>
            {fandoms.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
          </select>
          <input type="number" min="0" max="100" step="1" className="hos-input" placeholder="Weight %" value={form.weight_percentage} onChange={(e) => setForm({ ...form, weight_percentage: e.target.value })} data-testid="std-weight"/>
          <select className="hos-input" value={form.scoring_type} onChange={(e) => setForm({ ...form, scoring_type: e.target.value })} data-testid="std-scoring">
            <option value="manual">Manual (manager rates)</option>
            <option value="auto_cleanliness">Auto — from Cleanliness Status</option>
          </select>
        </div>
        <button className="hos-btn-primary mt-3 flex items-center gap-2" onClick={add} data-testid="std-add"><Plus size={14}/> Add Standard</button>
      </div>

      {Object.values(groups).map((g) => (
        <div key={`${g.zone_id}::${g.fandom_id || ""}`} className="hos-card p-4">
          <div className="flex items-center justify-between mb-2">
            <div>
              <div className="font-heading text-lg">{zName(g.zone_id)} <span className="text-[color:var(--text-secondary)] text-sm">· {fName(g.fandom_id)}</span></div>
              <div className="text-[11px] text-[color:var(--text-secondary)]">Weighted set — total must equal 100 %</div>
            </div>
            <div className={`text-sm font-heading ${Math.abs(g.total - 100) < 0.5 ? "text-emerald-400" : "text-[color:var(--warning)]"}`}>
              Total {g.total}% {Math.abs(g.total - 100) < 0.5 ? "✓" : "⚠"}
            </div>
          </div>
          <table className="w-full text-sm">
            <thead className="text-left label-caps"><tr><th className="p-2">Name</th><th>Weight</th><th>Scoring</th><th>Active</th><th></th></tr></thead>
            <tbody>
              {g.items.sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0)).map((s) => (
                <tr key={s.id} className="border-t border-[color:var(--border)]">
                  <td className="p-2">
                    <div className="font-semibold">{s.name}</div>
                    {s.description && <div className="text-[11px] text-[color:var(--text-secondary)]">{s.description}</div>}
                  </td>
                  <td>
                    <input type="number" min="0" max="100" defaultValue={s.weight_percentage} onBlur={(e) => update(s.id, { weight_percentage: Number(e.target.value) })} className="hos-input w-20" data-testid={`std-w-${s.id}`}/>
                  </td>
                  <td className="text-xs">{s.scoring_type}</td>
                  <td>
                    <input type="checkbox" checked={s.active !== false} onChange={(e) => update(s.id, { active: e.target.checked })} data-testid={`std-active-${s.id}`}/>
                  </td>
                  <td>
                    <button className="text-[color:var(--error)] flex items-center gap-1" onClick={() => del(s.id)} data-testid={`std-del-${s.id}`}>
                      <Trash2 size={12}/> Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}
      {items.length === 0 && (
        <div className="hos-card p-6 text-center text-sm text-[color:var(--text-secondary)]">
          No Display Standards yet. Add at least one per Zone (weights must total 100 % to publish a Display Score).
        </div>
      )}
    </div>
  );
}
